<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ColorInput from 'dashboard/components-next/colorpicker/ColorInput.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import {
  applyPreset,
  GOOGLE_FONT_OPTIONS,
  RADIUS_OPTIONS,
  resolveFontFamily,
  SPACING_OPTIONS,
  STYLE_PRESET_OPTIONS,
} from '../lib/portalTheme';

const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  embedded: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();

const update = (key, value) => {
  emit('update:modelValue', { ...props.modelValue, [key]: value });
};

const onPresetChange = value => {
  emit('update:modelValue', applyPreset(props.modelValue, value));
};

const fontModel = computed({
  get: () => resolveFontFamily(props.modelValue.font),
  set: value => update('font', value || 'Inter'),
});

const presetOptions = computed(() =>
  STYLE_PRESET_OPTIONS.map(opt => ({
    value: opt.value,
    label: t(
      `PLANNING.EVENT_PORTALS.EDITOR.THEME.PRESETS.${opt.value.toUpperCase()}`
    ),
  }))
);

const radiusOptions = computed(() =>
  RADIUS_OPTIONS.map(opt => ({
    value: opt.value,
    label: t(
      `PLANNING.EVENT_PORTALS.EDITOR.THEME.RADIUS_${opt.value.toUpperCase()}`
    ),
  }))
);

const spacingOptions = computed(() =>
  SPACING_OPTIONS.map(opt => ({
    value: opt.value,
    label: t(
      `PLANNING.EVENT_PORTALS.EDITOR.THEME.SPACING_${opt.value.toUpperCase()}`
    ),
  }))
);
</script>

<template>
  <component
    :is="embedded ? 'div' : 'aside'"
    :class="
      embedded
        ? 'flex flex-col'
        : 'w-72 shrink-0 border-l border-n-weak bg-n-solid-2 flex flex-col overflow-y-auto'
    "
  >
    <div v-if="!embedded" class="px-4 py-3 border-b border-n-weak">
      <p class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide">
        {{ t('PLANNING.EVENT_PORTALS.EDITOR.THEME.TITLE') }}
      </p>
    </div>

    <div class="p-4 grid grid-cols-[88px_1fr] items-center gap-x-2 gap-y-3">
      <label class="text-xs font-medium text-n-slate-11 truncate">
        {{ t('PLANNING.EVENT_PORTALS.EDITOR.THEME.PRESET') }}
      </label>
      <div class="min-w-0">
        <Input
          type="select"
          size="xs"
          :model-value="modelValue.style || 'modern'"
          :options="presetOptions"
          @update:model-value="onPresetChange"
        />
      </div>

      <label class="text-xs font-medium text-n-slate-11 truncate">
        {{ t('PLANNING.EVENT_PORTALS.EDITOR.THEME.ACCENT') }}
      </label>
      <div class="min-w-0">
        <ColorInput
          :model-value="modelValue.accent_color || '#7c3aed'"
          @update:model-value="update('accent_color', $event || '#7c3aed')"
        />
      </div>

      <label class="text-xs font-medium text-n-slate-11 truncate">
        {{ t('PLANNING.EVENT_PORTALS.EDITOR.THEME.BACKGROUND') }}
      </label>
      <div class="min-w-0">
        <ColorInput
          :model-value="modelValue.bg_color || '#ffffff'"
          @update:model-value="update('bg_color', $event || '#ffffff')"
        />
      </div>

      <label class="text-xs font-medium text-n-slate-11 truncate">
        {{ t('PLANNING.EVENT_PORTALS.EDITOR.THEME.TEXT') }}
      </label>
      <div class="min-w-0">
        <ColorInput
          :model-value="modelValue.text_color || '#1e293b'"
          @update:model-value="update('text_color', $event || '#1e293b')"
        />
      </div>

      <label class="text-xs font-medium text-n-slate-11 truncate">
        {{ t('PLANNING.EVENT_PORTALS.EDITOR.THEME.FONT') }}
      </label>
      <div class="min-w-0">
        <ComboBox
          v-model="fontModel"
          size="xs"
          :options="GOOGLE_FONT_OPTIONS"
          :placeholder="
            t('PLANNING.EVENT_PORTALS.EDITOR.THEME.FONT_PLACEHOLDER')
          "
        />
      </div>

      <label class="text-xs font-medium text-n-slate-11 truncate">
        {{ t('PLANNING.EVENT_PORTALS.EDITOR.THEME.RADIUS') }}
      </label>
      <div class="min-w-0">
        <Input
          type="select"
          size="xs"
          :model-value="modelValue.border_radius || 'md'"
          :options="radiusOptions"
          @update:model-value="update('border_radius', $event)"
        />
      </div>

      <label class="text-xs font-medium text-n-slate-11 truncate">
        {{ t('PLANNING.EVENT_PORTALS.EDITOR.THEME.SPACING') }}
      </label>
      <div class="min-w-0">
        <Input
          type="select"
          size="xs"
          :model-value="modelValue.spacing || 'normal'"
          :options="spacingOptions"
          @update:model-value="update('spacing', $event)"
        />
      </div>

      <label
        class="text-xs font-medium text-n-slate-11 truncate self-start pt-1.5"
      >
        {{ t('PLANNING.EVENT_PORTALS.EDITOR.THEME.CUSTOM_CSS') }}
      </label>
      <div class="min-w-0 space-y-1">
        <textarea
          :value="modelValue.custom_css || ''"
          class="reset-base w-full h-24 text-xs font-mono border border-n-slate-5 rounded-lg bg-n-slate-1 text-n-slate-12 px-2.5 py-2 resize-none outline-none focus:border-n-brand-7"
          :placeholder="
            t('PLANNING.EVENT_PORTALS.EDITOR.THEME.CUSTOM_CSS_HINT')
          "
          spellcheck="false"
          @input="update('custom_css', $event.target.value)"
        />
        <p class="text-[10px] text-n-slate-9">
          {{ t('PLANNING.EVENT_PORTALS.EDITOR.THEME.CUSTOM_CSS_HELP') }}
        </p>
      </div>
    </div>
  </component>
</template>
